import React, { RefObject, useEffect } from 'react'
import moment from 'moment'
import { canonicalStateForLabel, externalStatus } from '../../lib/status'
import { StateChange } from '../../types/Order'
import { CardWrapper } from '../../v2/styles'
import { useTracking } from '../../hooks/useTracking'

type ConditionalCardWrapperProps = {
  children: React.ReactNode
}
export const ConditionalCardWrapper = ({
  children,
}: ConditionalCardWrapperProps) => {
  const { hasMap, hasWidget } = useTracking()
  return hasMap || hasWidget ? <CardWrapper>{children}</CardWrapper> : children
}

export function getStateChangesWithDate(
  stateChanges: StateChange[]
): StateChange[] {
  return stateChanges.map((s: any) => ({
    ...s,
    occurredAt: new Date(s.occurredAt),
  }))
}

export function getOrderedStateChanges(
  stateChanges: StateChange[]
): StateChange[] {
  return stateChanges.sort((a: any, b: any) => b.occurredAt - a.occurredAt)
}

// Picks the "Expected collection" string shown in the tracking-link header
// for partner_return (RMA) orders.
//
// Once the order is `out_for_collection`, we render today regardless of the
// state-change anchor — see sc-26136. Otherwise we fall back to the legacy
// heuristic (anchor + 1 day @ 22:00 for non-remote, anchor + 5 days for
// Scheduled Routes / null parentGroup). The legacy heuristic is approximate
// and will be replaced by a backend `collection_before` stamp tracked in
// sc-26135; this helper is a holding place until that lands.
export function getPartnerReturnDisplayDate({
  internalState,
  anchorDate,
  zoneParentGroup,
  now = new Date(),
}: {
  internalState?: string
  anchorDate: Date | string
  zoneParentGroup?: string | null
  now?: Date
}): string {
  if (internalState === 'out_for_collection') {
    return moment(now).format('dddd [the] Do [of] MMMM')
  }
  const willBeTomorrow =
    zoneParentGroup !== null && zoneParentGroup !== 'Scheduled Routes'
  return willBeTomorrow
    ? moment(anchorDate)
        .set({ hour: 22, minute: 0 })
        .add(1, 'day')
        .format('dddd [the] Do [of] MMMM [before] hh:mm a')
    : moment(anchorDate)
        .add(5, 'day')
        .format('[until] dddd [the] Do [of] MMMM')
}

export function getUniqueStateChanges(
  stateChanges: StateChange[]
): (StateChange | undefined)[] {
  const uniqueStates: Set<string> = new Set(
    stateChanges.map((stateChange) => externalStatus[stateChange.toState])
  )

  return Array.from(uniqueStates).map((uniqueState) => {
    const matching = stateChanges.filter(
      (stateChange) => externalStatus[stateChange.toState] === uniqueState
    )
    // For labels backed by multiple internal states, prefer the canonical one
    // (e.g. "Collected" should reflect `collected`, not a later `at_depot`).
    const canonical = canonicalStateForLabel[uniqueState]
    if (canonical) {
      const canonicalMatch = matching.find((sc) => sc.toState === canonical)
      if (canonicalMatch) return canonicalMatch
    }
    // Fall back to the most recent matching state change. Input is sorted DESC
    // by getOrderedStateChanges, so matching[0] is the newest — preserves the
    // existing "latest attempt" behavior for cases like multiple
    // `out_for_delivery` retries.
    return matching[0]
  })
}

type AnyEvent = MouseEvent | TouchEvent

export const useOnClickOutside = (
  ref: RefObject<HTMLElement>,
  handler: (event: AnyEvent) => void
) => {
  useEffect(() => {
    const listener = (event: AnyEvent) => {
      const target = event.target as Node
      if (!ref.current || ref.current.contains(target)) {
        return
      }
      handler(event)
    }

    document.addEventListener('mousedown', listener)
    document.addEventListener('touchstart', listener)
    return () => {
      document.removeEventListener('mousedown', listener)
      document.removeEventListener('touchstart', listener)
    }
  }, [ref, handler])
}

export const EventMapper: {
  [key: string]: {
    event: string
    action: string
    category: string
  }
} = {
  'call driver': {
    event: 'Call Driver Button',
    action: 'Click',
    category: 'Contact',
  },
  'change address': {
    event: 'Change address Link',
    action: 'Click',
    category: 'Contact',
  },
  'change address unavailable': {
    event: 'Change address Link',
    action: 'Click',
    category: 'Contact',
  },
  'leave at door': {
    event: 'Leave At Door Switch',
    action: 'Toggle',
    category: 'Tracking',
  },
  whatsapp: {
    event: 'Whatsapp Button',
    action: 'Click',
    category: 'Contact',
  },
  'phone number': {
    event: 'Phone Number Button',
    action: 'Click',
    category: 'Contact',
  },
  'track dhl': {
    event: 'Track DHL Button',
    action: 'Click',
    category: 'Tracking',
  },
}
